import type { ChatItem, ToolUseItem } from './harness.types';
import type { ViewedDocumentContext } from './projects/requests.types';

type EditTarget = {
  getTool: string;
  idKeys: readonly string[];
  /** Where the read's body sits, when not in one of `BODY_KEYS`. */
  bodyKeys?: readonly string[];
  /** The viewed tab that shows the target, for a target no document id names. */
  viewedTab?: string;
};

const EDIT_TARGETS: Record<string, EditTarget> = {
  edit_consult_note: {
    getTool: 'get_consult_note',
    idKeys: ['session_id'],
    viewedTab: 'note',
  },
  edit_session_document: {
    getTool: 'get_session_document',
    idKeys: ['session_id', 'document_id'],
  },
  edit_session_context: {
    getTool: 'get_session_context',
    idKeys: ['session_id'],
    bodyKeys: ['text'],
  },
  update_session_context_document: {
    getTool: 'get_session_context_document',
    idKeys: ['session_id', 'context_document_id'],
  },
};

type DraftEdit = Pick<ToolUseItem, 'name' | 'input' | 'partialInput'> &
  Partial<Pick<ToolUseItem, 'id'>>;

const BODY_KEYS = ['note', 'edited_note', 'content'] as const;

const bodyFromRecord = (
  record: Record<string, unknown>,
  keys: readonly string[]
): string | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return undefined;
};

const resultText = (item: ToolUseItem): string | undefined => {
  const content = item.result?.content;
  if (typeof content === 'string' && content) return content;
  if (!Array.isArray(content)) return undefined;
  const text = content
    .map((part) =>
      part &&
      typeof part === 'object' &&
      typeof (part as { text?: unknown }).text === 'string'
        ? (part as { text: string }).text
        : ''
    )
    .join('');
  return text || undefined;
};

const bodyFromGetResult = (
  item: ToolUseItem,
  keys: readonly string[]
): string | undefined => {
  if (item.result?.is_error) return undefined;
  const data = item.result_provider_metadata?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const fromData = bodyFromRecord(data as Record<string, unknown>, keys);
    if (fromData) return fromData;
  }
  const text = resultText(item);
  if (!text) return undefined;
  const jsonSlice = text.trim().startsWith('{') ? text.split('\n\n')[0] : undefined;
  if (!jsonSlice) return undefined;
  try {
    const parsed: unknown = JSON.parse(jsonSlice);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return bodyFromRecord(parsed as Record<string, unknown>, keys);
    }
  } catch {
    return undefined;
  }
  return undefined;
};

/** Index of the quote that closes the JSON string opening at `start`, or -1. */
const closingQuote = (json: string, start: number): number => {
  for (let index = start + 1; index < json.length; index += 1) {
    if (json[index] === '\\') index += 1;
    else if (json[index] === '"') return index;
  }
  return -1;
};

/**
 * The closed string values at the top level of a partial JSON object. Only
 * closed strings count, so a half-streamed id never matches a shorter one, and
 * only top-level keys, so an id quoted inside a streamed body never does.
 */
const streamedTopLevelStrings = (partialInput: string): Map<string, string> => {
  const values = new Map<string, string>();
  let depth = 0;
  let key: string | undefined;
  let afterColon = false;
  for (let index = 0; index < partialInput.length; index += 1) {
    const char = partialInput[index];
    if (char === '"') {
      const end = closingQuote(partialInput, index);
      if (end < 0) break;
      if (depth === 1) {
        let text: unknown;
        try {
          text = JSON.parse(partialInput.slice(index, end + 1));
        } catch {
          text = undefined;
        }
        if (afterColon && key !== undefined && typeof text === 'string') {
          values.set(key, text);
        } else if (!afterColon && typeof text === 'string') {
          key = text;
        }
      }
      index = end;
    } else if (char === '{' || char === '[') {
      depth += 1;
    } else if (char === '}' || char === ']') {
      depth -= 1;
    } else if (depth === 1 && char === ':') {
      afterColon = true;
    } else if (depth === 1 && char === ',') {
      key = undefined;
      afterColon = false;
    }
  }
  return values;
};

const editIds = (edit: DraftEdit, keys: readonly string[]) => {
  let streamed: Map<string, string> | undefined;
  return keys.map((key) => {
    const value = edit.input?.[key];
    if (typeof value === 'string') return [key, value] as const;
    streamed ??= streamedTopLevelStrings(edit.partialInput ?? '');
    return [key, streamed.get(key)] as const;
  });
};

type EditIds = ReturnType<typeof editIds>;

const readBody = (
  items: ChatItem[],
  edit: DraftEdit,
  target: EditTarget,
  ids: EditIds
): string | undefined => {
  const own = edit.id
    ? items.findIndex((item) => item.kind === 'tool_use' && item.id === edit.id)
    : -1;
  for (let index = (own < 0 ? items.length : own) - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (!item || item.kind !== 'tool_use') continue;
    const isRead = item.name === target.getTool;
    if (!isRead && item.name !== edit.name) continue;
    if (!ids.every(([key, id]) => item.input?.[key] === id)) continue;
    if (!isRead) {
      if (item.status === 'complete' && !item.result?.is_error) return undefined;
      continue;
    }
    const body = bodyFromGetResult(item, target.bodyKeys ?? BODY_KEYS);
    if (body) return body;
  }
  return undefined;
};

/** The on-screen document, with the session it belongs to. */
type ViewedDraftSource = ViewedDocumentContext & { session_id?: string };

const viewedBody = (
  target: EditTarget,
  ids: EditIds,
  viewed: ViewedDraftSource | undefined
): string | undefined => {
  if (!viewed?.session_id) return undefined;
  const shown = ids.every(([key, id]) =>
    key === 'session_id' ? id === viewed.session_id : id === viewed.document_id
  );
  const onTab = ids.length > 1 || viewed.tab === target.viewedTab;
  return shown && onTab ? viewed.content : undefined;
};

/**
 * The text a span draft applies to, so the card can preview the full result:
 * the latest get_* read of the same target before the edit (every id in
 * `idKeys` equal), else the on-screen document when it shows that target.
 * A read taken before an earlier completed edit of the target no longer shows
 * it and is skipped. Undefined while the edit's ids are unknown.
 */
export const sessionDraftBeforeText = (
  items: ChatItem[] | undefined,
  edit: DraftEdit,
  viewed?: ViewedDraftSource
): string | undefined => {
  const target = EDIT_TARGETS[edit.name];
  if (!target) return undefined;
  const ids = editIds(edit, target.idKeys);
  if (ids.some(([, id]) => !id)) return undefined;
  return (
    (items?.length ? readBody(items, edit, target, ids) : undefined) ??
    viewedBody(target, ids, viewed)
  );
};
